<template>
    <div v-if="permittedLicense">
        <div class="chat-icon-container" @click="openChat">
            <i class="pi pi-comments"></i>
        </div>
        <Dialog 
            v-model:visible="isDialogVisible" 
            :id="question.id"
            :header="'Help about question ' + question.id + ': ' + question.text[currentLanguage]" 
            :modal="false" 
            class="chat-modal"
            :dismissableMask="false"
            :style="dialogPosition"
            :position="dialogPositionClass"
            @hide="onDialogClose"
            @mousedown="bringToFront"
        >
            <div class="chat-content">
                <div v-if="currentChat.hasStarted" class="helperbot" ref="helperbot">
                    <div v-for="(message, index) in sortedMessages" 
                        :key="index" 
                        :class="['message', message.role, { 'error': message.hasError }]">
                        <div class="message-content" v-html="formatContent(message.content)"></div>
                        <div class="message-time">
                            {{ formatTime(message.tstamp) }}
                            <i v-if="message.role === 'user' && !message.hasError" class="pi pi-check message-status-icon"></i>
                            <i v-if="message.role === 'user' && message.hasError" class="pi pi-times-circle message-status-icon"></i>
                        </div>
                    </div>
                    
                    <div v-if="isWaitingResponse" class="message server typing-indicator">
                        <div class="dots">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                </div>

                <div v-else-if="hasTermsAndPrivacyAccepted" class="initial-message">
                    <div class="initial-message-text">
                        {{ $t('helperBot.start_chat_message') }}
                    </div>
                </div>

                <div v-else class="initial-message">
                    <div class="initial-message-text">
                        {{ $t('helperBot.initial_message') }}
                    </div>
                    <div class="terms-privacy-container">
                        <div class="checkbox-container">
                            <Checkbox v-model="termsAccepted" :binary="true" inputId="terms" />
                            <label for="terms" class="checkbox-label">
                                {{ $t('helperBot.accept_terms') }} <a href="/assets/legal/HelperBot_Termini_Condizioni.pdf" target="_blank">{{ $t('helperBot.terms_conditions') }}</a>
                            </label>
                        </div>
                        <div class="checkbox-container">
                            <Checkbox v-model="privacyAccepted" :binary="true" inputId="privacy" />
                            <label for="privacy" class="checkbox-label">
                                {{ $t('helperBot.accept_privacy') }} <a href="https://www.privacylab.it/informativa.php?23248480292" target="_blank">{{ $t('helperBot.privacy_policy') }}</a>
                            </label>
                        </div>
                    </div>
                </div>
                
                <div v-if="hasTermsAndPrivacyAccepted" class="chat-input p-mt-3">
                    <div class="input-wrapper">
                        <InputText 
                            v-model="userInput" 
                            :placeholder="$t('helperBot.placeholder')"
                            class="p-mb-2" 
                            style="width: 100%; height: 40px;" 
                            @keyup.enter="sendMessage"
                            :maxlength="MAX_MESSAGE_LENGTH"
                            :disabled="!canSendMessage"
                        />
                        <small :class="['char-counter', { 'near-limit': isNearCharLimit }]">
                            {{ userInput.length }}/{{ MAX_MESSAGE_LENGTH }}
                        </small>
                    </div>
                    <Button 
                        @click="sendMessage" 
                        :label="$t('general.send')" 
                        class="p-button-primary" 
                        style="height: 40px;"
                        :disabled="!isValidInput || isWaitingResponse || !canSendMessage"
                    />
                </div>
            </div>
        </Dialog>
    </div>
</template>

<script>
import ChatService from '@/services/ChatService';
import { useStore } from '@/store';
import { resetChat } from '@/store';
import { useRoute } from 'vue-router';
import { ENV_VARS } from '@/env_vars';

export default {
    name: 'HelperBot',
    props: {
        question: {
            type: Object,
            required: true
        }
    },
    setup() {
        const { state, updateChat } = useStore();
        const route = useRoute();
        
        let engineValue = null;

        const pathSegments = route.path.split('/').filter(segment => segment);
        if (pathSegments.length > 0) {
            engineValue = pathSegments[0];
        }
        
        const chatService = engineValue ? new ChatService(engineValue) : null;
        
        if (!chatService) {
            console.error('Could not initialize ChatService - engine parameter not found in route');
        }

        const permittedLicense = ENV_VARS.VUE_APP_PERMITTED_LICENSES && 
                                ENV_VARS.VUE_APP_PERMITTED_LICENSES.split(',')
                                    .map(license => license.trim().toLowerCase())
                                    .includes('helperbot');

        return {
            state,
            updateChat,
            route,
            chatService,
            engineValue,
            permittedLicense
        };
    },
    data() {
        let termsAccepted = false;
        let privacyAccepted = false;
        
        try {
            const termsData = localStorage.getItem('helperbot_terms_accepted');
            if (termsData) {
                const parsedData = JSON.parse(termsData);
                if (new Date().getTime().toString() > parsedData.expires) {
                    localStorage.removeItem('helperbot_terms_accepted');
                } else {
                    termsAccepted = parsedData.value;
                }
            }
            
            const privacyData = localStorage.getItem('helperbot_privacy_accepted');
            if (privacyData) {
                const parsedData = JSON.parse(privacyData);
                if (new Date().getTime().toString() > parsedData.expires) {
                    localStorage.removeItem('helperbot_privacy_accepted');
                } else {
                    privacyAccepted = parsedData.value;
                }
            }
        } catch (e) {
            console.error('Errore nella lettura dei dati dal localStorage', e);
        }
        
        return {
            isDialogVisible: false,
            userInput: '',
            currentLanguage: 'it',
            MAX_MESSAGE_LENGTH: 250, // char limit per message
            isWaitingResponse: false, // track waiting status
            dialogOffset: { x: '0px', y: '0px' },
            dialogPositionClass: 'center',
            baseZIndex: 1100, // base z-index value for dialogs
            termsAccepted,
            privacyAccepted
        };
    },
    computed: {
        isValidInput() {
            return this.userInput.trim().length > 0 && 
                   this.userInput.length <= this.MAX_MESSAGE_LENGTH;
        },
        isNearCharLimit() {
            return this.userInput.length > this.MAX_MESSAGE_LENGTH * 0.9;
        },
        currentChat() {
            return this.state.chats[this.question.id] || { messages: [], hasStarted: false };
        },
        sortedMessages() {
            if (!this.currentChat || !this.currentChat.messages) return [];
            return [...this.currentChat.messages].sort((a, b) => {
                return new Date(a.tstamp) - new Date(b.tstamp);
            });
        },
        dialogPosition() {
            return {
                marginLeft: this.dialogOffset.x,
                marginTop: this.dialogOffset.y
            };
        },
        hasTermsAndPrivacyAccepted() {
            return this.termsAccepted && this.privacyAccepted;
        },
        canSendMessage() {
            return this.hasTermsAndPrivacyAccepted || this.currentChat.hasStarted;
        }
    },
    watch: {
        termsAccepted(newValue) {
            if (newValue) {
                const obj = { 
                    value: true, 
                    expires: new Date().setDate(new Date().getDate() + 7).toString() 
                };
                localStorage.setItem('helperbot_terms_accepted', JSON.stringify(obj));
            } else {
                localStorage.removeItem('helperbot_terms_accepted');
            }
        },
        privacyAccepted(newValue) {
            if (newValue) {
                const obj = { 
                    value: true, 
                    expires: new Date().setDate(new Date().getDate() + 7).toString() 
                };
                localStorage.setItem('helperbot_privacy_accepted', JSON.stringify(obj));
            } else {
                localStorage.removeItem('helperbot_privacy_accepted');
            }
        }
    },
    mounted() {
        window.addEventListener('storage', this.handleStorageChange);
    },
    beforeUnmount() {
        window.removeEventListener('storage', this.handleStorageChange);
    },
    methods: {
        handleStorageChange(event) {
            if (event.key === 'helperbot_terms_accepted' && event.newValue) {
                try {
                    const parsedData = JSON.parse(event.newValue);
                    if (new Date().getTime().toString() > parsedData.expires) {
                        this.termsAccepted = false;
                        localStorage.removeItem('helperbot_terms_accepted');
                    } else {
                        this.termsAccepted = parsedData.value;
                    }
                } catch (e) {
                    this.termsAccepted = false;
                }
            }
            if (event.key === 'helperbot_privacy_accepted' && event.newValue) {
                try {
                    const parsedData = JSON.parse(event.newValue);
                    if (new Date().getTime().toString() > parsedData.expires) {
                        this.privacyAccepted = false;
                        localStorage.removeItem('helperbot_privacy_accepted');
                    } else {
                        this.privacyAccepted = parsedData.value;
                    }
                } catch (e) {
                    this.privacyAccepted = false;
                }
            }
        },
        openChat() {
            // generate random offset to move dialog on opening
            const randomX = Math.floor(Math.random() * 80) - 40;
            const randomY = Math.floor(Math.random() * 80) - 40;
            
            this.dialogOffset = {
                x: `${randomX}px`,
                y: `${randomY}px`
            };
            
            try {
                const termsData = localStorage.getItem('helperbot_terms_accepted');
                if (termsData) {
                    const parsedData = JSON.parse(termsData);
                    if (new Date().getTime().toString() > parsedData.expires) {
                        localStorage.removeItem('helperbot_terms_accepted');
                        this.termsAccepted = false;
                    } else {
                        this.termsAccepted = parsedData.value;
                    }
                } else {
                    this.termsAccepted = false;
                }
                
                const privacyData = localStorage.getItem('helperbot_privacy_accepted');
                if (privacyData) {
                    const parsedData = JSON.parse(privacyData);
                    if (new Date().getTime().toString() > parsedData.expires) {
                        localStorage.removeItem('helperbot_privacy_accepted');
                        this.privacyAccepted = false;
                    } else {
                        this.privacyAccepted = parsedData.value;
                    }
                } else {
                    this.privacyAccepted = false;
                }
            } catch (e) {
                console.error('Errore nella lettura dei dati dal localStorage', e);
                this.termsAccepted = false;
                this.privacyAccepted = false;
            }
            
            this.isDialogVisible = true;
        },
        onDialogClose() {
            resetChat(this.updateChat, this.question.id);
            this.userInput = '';
        },
        formatTime(tstamp) {
            return new Date(tstamp).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        },
        formatContent(content) {
            if (!content) return '';
            
            const htmlContent = content
                .replace(/\n/g, '<br>')
                .replace(/(\d+\.\s+)([^\n<]+)/g, '<strong>$1</strong>$2')
                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
                
            return htmlContent;
        },
        scrollToBottom() {
            this.$nextTick(() => {
                if (this.$refs.helperbot) {
                    this.$refs.helperbot.scrollTop = this.$refs.helperbot.scrollHeight;
                }
            });
        },
        extractCategory(question) {
            
            if (!question || !question.text || !question.text[this.currentLanguage]) {
                return '';
            }
            
            const text = question.text[this.currentLanguage];
            
            const match = text.match(/\[(.*?)\]/);
            
            if (match && match[1]) {
                const category = match[1].trim();
                return category;
            }
            
            return '';
        },
        async sendMessage() {
            if (!this.isValidInput || this.isWaitingResponse) {
                return;
            }

            const newMessage = {
                content: this.userInput.trim(),
                tstamp: new Date().toISOString(),
                role: 'user',
                hasError: false
            };

            const userMessageIndex = this.currentChat.messages ? this.currentChat.messages.length : 0;

            this.updateChat(this.question.id, {
                messages: [...(this.currentChat.messages || []), newMessage],
                hasStarted: true
            });

            this.userInput = '';
            this.scrollToBottom();
            this.isWaitingResponse = true;

            if (this.chatService) {
                try {
                    const payload = {
                        context: {
                            text: this.question.text[this.currentLanguage],
                            category: this.extractCategory(this.question)
                        },
                        history: this.currentChat.messages.slice(-10).map(msg => ({
                            content: msg.content,
                            tstamp: msg.tstamp,
                            role: msg.role
                        }))
                    };
                    
                    const response = await this.chatService.sendMessage(payload);
                    
                    // maps error codes
                    let content = response.content;
                    let hasError = false;
                    
                    if (response.code) {
                        hasError = true;
                        
                        const code = String(response.code);
                        
                        if (code === '102') {
                            content = this.$t('helperBot.length_exceeded');
                        } else if (code === '103') {
                            content = this.$t('helperBot.license_expired');
                        } else {
                            content = this.$t('helperBot.cannot_send');
                        }
                        
                        const allMessages = [...this.currentChat.messages];
                        if (userMessageIndex < allMessages.length && allMessages[userMessageIndex].role === 'user') {

                            allMessages[userMessageIndex] = {
                                ...allMessages[userMessageIndex],
                                hasError: true
                            };
                            
                            this.updateChat(this.question.id, {
                                messages: allMessages,
                                hasStarted: true
                            });
                        }
                    }
                    
                    const serverMessage = {
                        content: content,
                        tstamp: response.tstamp || new Date().toISOString(),
                        role: response.role || 'assistant',
                        hasError: hasError
                    };
                    
                    this.updateChat(this.question.id, {
                        messages: [...this.currentChat.messages, serverMessage],
                        hasStarted: true
                    });
                } catch (error) {
                    console.error('Error sending message:', error);
                    
                    if (error.response?.status === 403 || 
                        (error.message && (
                            error.message.toLowerCase().includes('licenza') ||
                            error.message.toLowerCase().includes('license')
                        ))) {
                        
                        const allMessages = [...this.currentChat.messages];
                        if (userMessageIndex < allMessages.length && allMessages[userMessageIndex].role === 'user') {
                            allMessages[userMessageIndex] = {
                                ...allMessages[userMessageIndex],
                                hasError: true
                            };
                            
                            this.updateChat(this.question.id, {
                                messages: allMessages,
                                hasStarted: true
                            });
                        }
                        
                        const serverMessage = {
                            content: this.$t('helperBot.license_expired'),
                            tstamp: new Date().toISOString(),
                            role: 'assistant',
                            hasError: true
                        };
                        
                        this.updateChat(this.question.id, {
                            messages: [...this.currentChat.messages, serverMessage],
                            hasStarted: true
                        });
                    }
                } finally {
                    this.isWaitingResponse = false;
                    this.scrollToBottom();
                }
            } else {
                console.error('Cannot send message');
                this.isWaitingResponse = false;
            }
        },
        // brings the dialog to the front, based on PrimeVue class p-dialog-mask
        bringToFront(event) {
            const currentDialog = event.currentTarget;
            const dialogId = currentDialog.id;
            
            if (!dialogId) return;
            
            const allDialogMasks = document.querySelectorAll('.p-dialog-mask');
            
            allDialogMasks.forEach(mask => {
                mask.style.zIndex = this.baseZIndex.toString();
            });
            
            const currentDialogMask = document.querySelector(`.p-dialog-mask > .p-dialog[id="${dialogId}"]`).closest('.p-dialog-mask');
            
            if (currentDialogMask) {
                currentDialogMask.style.zIndex = (this.baseZIndex + 1).toString();
            }
        }
    }
};
</script>


<style>
.chat-modal {
    width: 50vw;
}

.message-content {
    white-space: pre-line;
    line-height: 1.5;
}

@media only screen and (max-width: 600px) {
    .chat-modal {
        width: 90%;
    }
}
</style>

<style lang="scss" scoped>
.chat-icon-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #03a9f4;
    color: white;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #0056b3;
    }

    i {
        font-size: 1rem;
    }
}

.chat-content {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    height: 60vh;

    .initial-message {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #676;
        background-color: #f8f9fa;
        border-radius: 4px;
    }

    .initial-message-text {
        font-size: 0.9rem;
        color: #333;
        padding: 1rem;
        margin-bottom: 1.5rem;
        text-align: center;
        width: 100%;
    }

    .helperbot {
        flex: 1;
        overflow-y: auto;
        padding: 1rem;
        background-color: #f8f9fa;
        border-radius: 4px;

        .message {
            margin-bottom: 1rem;
            max-width: 70%;
            
            .message-content {
                padding: 0.8rem;
                border-radius: 12px;
                word-wrap: break-word;
            }

            .message-time {
                font-size: 0.8rem;
                color: #676;
                margin-top: 0.2rem;
                display: flex;
                align-items: center;
                
                .message-status-icon {
                    margin-left: 4px;
                    
                    &.pi-check {
                        color: green;
                    }
                    
                    &.pi-times-circle {
                        color: red;
                    }
                }
            }

            &.assistant {
                .message-content {
                    background-color: #d3d3d3;
                    color: #000;
                }
                
                &.error .message-content {
                    background-color: #ffebee;
                    color: #d32f2f;
                }
            }

            &.user {
                margin-left: auto;
                
                .message-content {
                    background-color: #007bff;
                    color: white;
                }

                .message-time {
                    text-align: right;
                    justify-content: flex-end;
                }
            }
        }
    }

    .chat-input {
        display: flex;
        gap: 0.5rem;

        .p-inputtext {
            flex: 1;
        }
    }

    .input-wrapper {
        position: relative;
        flex: 1;

        .char-counter {
            position: absolute;
            right: 8px;
            bottom: -20px;
            font-size: 0.75rem;
            color: #676;

            &.near-limit {
                color: #ff9800;
            }

        }
    }

    .p-inputtext:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
    }
}

@keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
}

.near-limit {
    animation: pulse 1s infinite;
}

.typing-indicator {
    padding: 8px 12px !important;
    
    .dots {
        display: flex;
        gap: 4px;
        
        span {
            width: 8px;
            height: 8px;
            background: #676;
            border-radius: 50%;
            animation: bounce 1.5s infinite;
            
            &:nth-child(2) { animation-delay: 0.2s; }
            &:nth-child(3) { animation-delay: 0.4s; }
        }
    }
}

@keyframes bounce {
    0%, 60%, 100% {
        transform: translateY(0);
    }
    30% {
        transform: translateY(-4px);
    }
}

.message.server {
    align-self: flex-start;
    border-radius: 12px 12px 12px 0;
    margin: 4px 0;
    max-width: 80%;
    padding: 8px 12px;
}

.terms-privacy-container {
    margin-top: 1rem;
    padding: 1rem;
}

.checkbox-container {
    margin-bottom: 0.5rem;
}

.checkbox-label {
    margin-left: 0.5rem;
    font-size: 0.9rem;
}
</style>